<template>
  <b-modal
    v-model="show"
    size="lg"
    :modal-class="isLoading || isTimeOffDeleteLoading ? 'view-time-off-modal loading' : 'view-time-off-modal'"
    header-class="view-time-off-modal-header-custom"
    hide-footer
    centered
    @hide="closeModal"
  >
    <template #modal-header="{ close }">
      <ModalHeader :title="title" :onCloseButton="close" />
    </template>

    <div class="view-time-off-modal-body">
      <div v-if="showLoader" class="loading-overlay">
        <div class="loading-spinner"></div>
      </div>

      <div>
        <!-- Row 1: User and Time off type -->
        <div class="data-row">
          <div class="detail-section">
            <div class="detail-label">User</div>
            <div class="detail-value">{{ timeOff.user.name }} <a target="_blank" :href="`/time-off-report/${timeOff.user.id}`">(report)</a></div>
          </div>
          <div class="detail-section">
            <div class="detail-label">Time off type</div>
            <div class="detail-value d-flex align-items-center">
              <TimeOffIcon
                :name="timeOff.time_offs_settings.icon"
                :color="timeOff.time_offs_settings.color"
                className="time-off-icon mr-2"
              />
              {{ timeOff.time_offs_settings.name }}
            </div>
          </div>
        </div>

        <!-- Separator -->
        <div class="separator"></div>

        <!-- Row 2: Date from and Date to -->
        <div class="data-row">
          <div class="detail-section">
            <div class="detail-label">Date from</div>
            <div class="detail-value">{{ timeOff.date_from }}</div>
          </div>
          <div class="detail-section">
            <div class="detail-label">Date to</div>
            <div class="detail-value">{{ timeOff.date_to }}</div>
          </div>
        </div>

        <!-- Separator -->
        <div class="separator"></div>

        <!-- Row 3: Duration and Attachment file -->
        <div class="data-row">
          <div class="detail-section">
            <div class="detail-label">Duration</div>
            <div v-if="timeOff.days !== undefined" class="detail-value">{{ timeOff.days }} day{{ timeOff.days !== 1 ? 's' : '' }}</div>
          </div>
          <div class="detail-section">
            <div class="detail-label">Attachment file</div>
            <div class="detail-value">
              <a
                v-if="timeOff.download_url"
                target="_blank"
                :href="timeOff.download_url"
                :title="timeOff.file_name || 'Attachment file'"
                class="attachment-link"
              >
                <span class="material-symbols-outlined">
                  attachment
                </span>
                {{ timeOff.file_name || 'Download attachment' }}
              </a>
              <span v-else class="no-attachment">No attachment</span>
            </div>
          </div>
        </div>

        <!-- Separator -->
        <div class="separator"></div>

        <!-- Row 4: Comment -->
        <div class="data-row">
          <div class="detail-section">
            <div class="detail-label">Comment</div>
            <div class="detail-value comment-value">
              <span v-if="timeOff.comment" v-html="nlToBr(timeOff.comment)"></span>
              <span v-else class="no-comment">No comment</span>
            </div>
          </div>
        </div>

        <!-- Separator -->
        <div class="separator"></div>

        <!-- Row 5: Approval Status -->
        <div class="data-row">
          <div class="detail-section approval-section">
            <div v-if="isApproved" class="approval-status">
              <font-awesome-icon icon="check-square" size="1x" />
              <span class="approved-by">Approved by {{ approverInfo.name }} at {{ approverInfo.date }}</span>
            </div>
            <div v-if="isPending && !isApprovalMode" class="pending-status">
              <font-awesome-icon icon="question-circle" size="1x" />
              Pending approval
            </div>
          </div>
        </div>

        <div class="footer">
          <div class="">
            <span class="material-symbols-outlined info-icon">
              info
            </span>
            <span v-if="isPayable" class="info-text">
              This is payable time off
            </span>
            <span v-else class="info-text">
              This is not payable time off
            </span>
          </div>

          <div class="d-flex action-buttons">
            <button
              v-if="canDecline"
              @click="declineTimeOff"
              :disabled="isLoading"
              class="btn button btn-decline btn-danger-filled"
            >
              Decline
            </button>

            <button
              v-if="canApprove"
              @click="approveTimeOff"
              :disabled="isLoading"
              class="btn button btn-approve btn-success"
            >
              Approve
            </button>

            <button
              v-if="canDelete"
              @click="deleteTimeOff"
              :disabled="isLoading"
              class="btn button btn-delete btn-danger"
            >
              Delete
            </button>

            <button
              v-if="canEdit"
              @click="editTimeOff"
              :disabled="isLoading"
              class="btn button btn-edit btn-primary"
            >
              Edit
            </button>
          </div>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import ModalHeader from '@components/modals/ModalHeader.vue'
import TimeOffIcon from '@components/TimeOffIcon.vue'
import Moment from 'moment/moment'

export default {
  name: 'ViewTimeOffModal',
  components: {
    ModalHeader,
    TimeOffIcon
  },
  props: {
    value: Boolean,
    timeOff: {
      type: Object,
      default: () => ({})
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: 'View time off'
    },
    mode: {
      type: String,
      default: 'view'
    }
  },
  data () {
    return {
      isTimeOffDeleteLoading: false
    }
  },
  computed: {
    show: {
      get () {
        return this.value
      },
      set (val) {
        this.$emit('input', val)
      }
    },
    getUser () {
      return this.$store.getters.getUser
    },
    canApprove () {
      return this.isApprover && this.isPending && this.$can('update_all-time_offs')
    },
    canDecline () {
      return this.isApprover && this.isPending && this.$can('update_all-time_offs')
    },
    canEdit () {
      return this.isRequestor && this.$can('update_own-time_offs')
    },
    canDelete () {
      const isInDisableMode = this.isApprovalMode || (this.timeOff.user && this.timeOff.user.id !== this.getUser.id)
      return (this.isApprover || this.isRequestor) && this.$can('delete-time_offs') && !isInDisableMode
    },
    isApprover () {
      if (this.timeOff && this.timeOff.time_offs_settings) {
        const approverRoles = this.timeOff.time_offs_settings.approver_roles.map(function (role) {
          return role.name
        })
        const userRoles = this.getUser.user_roles.map(function (role) {
          return role.name
        })
        const userHasApproveRoles = approverRoles.filter(value => userRoles.includes(value))
        return userHasApproveRoles.length !== 0
      } else {
        return false
      }
    },
    isRequestor () {
      return this.timeOff.user && this.getUser.id === this.timeOff.user.id
    },
    isPending () {
      return this.timeOff.status === 'pending'
    },
    isPayable () {
      return this.timeOff.time_offs_settings && this.timeOff.time_offs_settings.is_payable
    },
    showLoader () {
      return this.isLoading || this.isTimeOffDeleteLoading
    },
    isApproved () {
      return this.timeOff.status === 'approved'
    },
    approverInfo () {
      if (this.timeOff.time_off_request) {
        return {
          'name': this.timeOff.time_off_request.approver.name,
          'date': Moment(this.timeOff.time_off_request.updated_at).format('YYYY-MM-DD HH:mm')
        }
      } else {
        return {
          'name': '',
          'date': ''
        }
      }
    },
    isApprovalMode () {
      return this.mode === 'approve'
    }
  },
  methods: {
    closeModal () {
      this.$emit('input', false)
    },
    nlToBr (text) {
      if (!text) return ''
      return text.replace(/(?:\r\n|\r|\n)/g, '<br>')
    },
    async approveTimeOff () {
      const confirm = await this.$root.$confirm.open(
        'Approve',
        `Are you sure you want to approve time off period from ${this.timeOff.date_from} to ${this.timeOff.date_to}?`
      )
      if (!confirm) {
        return
      }
      this.$emit('approve', this.timeOff)
    },
    async declineTimeOff () {
      const confirm = await this.$root.$confirm.open(
        'Decline',
        `Are you sure you want to decline time off period from ${this.timeOff.date_from} to ${this.timeOff.date_to}?`
      )
      if (!confirm) {
        return
      }
      this.$emit('decline', this.timeOff)
    },
    editTimeOff () {
      this.$emit('edit', this.timeOff)
    },
    async deleteTimeOff () {
      if (!this.$can('delete-time_offs')) {
        return false
      }
      const confirm = await this.$root.$confirm.open(
        'Delete',
        `Are you sure you want to delete time off period from ${this.timeOff.date_from} to ${this.timeOff.date_to}?`
      )
      if (!confirm) {
        return
      }

      const deleteHandler = this.handleTimeOffDelete()
      if (deleteHandler && deleteHandler.setLoading) {
        deleteHandler.setLoading(true)
      }

      this.$api.timeOffs.removeTimeOff(this.timeOff.id)
        .then(() => {
          this.$snotify.success(
            'Time off successfully deleted',
            'Success',
            this.$store.state.snotify
          )
          this.$emit('deletedTimeOff', { data: this.timeOff.id })
          this.closeModal()
        })
        .catch((error) => {
          this.errorNotifications(error)
        })
        .finally(() => {
          if (deleteHandler && deleteHandler.setLoading) {
            deleteHandler.setLoading(false)
          }
        })
    },
    handleTimeOffDelete () {
      return {
        setLoading: (loading) => this.setTimeOffDeleteLoading(loading)
      }
    },
    setTimeOffDeleteLoading (loading) {
      this.isTimeOffDeleteLoading = loading
    }
  }
}
</script>

<style lang="scss" scoped>
::v-deep .view-time-off-modal {
  .modal-dialog {
    width: 792px;
  }
}

.loader-container {
  position: relative;
  min-height: 200px;
}

.view-time-off-modal-body {
  position: relative;
  min-height: 60px;
}

::v-deep .view-time-off-modal-header-custom {
  padding-top: 20px;

  h5 {
    font-size: 18px;
    font-style: normal;
    font-weight: 500;
    line-height: 30px;
  }
}

.separator {
  height: 1px;
  background: #E4E7EE;
  margin: 16px 0;
}

.data-row {
  display: flex;
  gap: 32px;
}

.detail-section {
  min-width: 220px;
  font-size: 13px;
  line-height: 20px;

  .detail-label {
    font-weight: 500;
    margin-bottom: 8px;
  }

  .detail-value {
    font-weight: 400;

    a {
      text-decoration: none;
      color: #0052CC;
      cursor: pointer;
    }

    &.comment-value {
      max-height: 120px;
      overflow-y: auto;
    }
  }
}

.no-comment, .no-attachment {
  color: #6c757d;
  font-style: italic;
}

.attachment-link {
  display: flex;
  gap: 4px;
  text-decoration: none;
  border-radius: 100px;
  background: $bgColorBase;
  width: fit-content;
  padding: 0 6px;
  color:#3D5170;
  font-size: 14px;
  font-weight: 400;
  line-height: 24px;

  .material-symbols-outlined {
    font-size: 24px;
    color: #0052CC;
    vertical-align: middle;
  }
}

.footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 16px;

  .info-text {
    font-size: 13px;
    font-weight: 500;
    line-height: 24px;
  }

  .action-buttons {
    display: flex;
    gap: 16px;

    .btn {
      justify-content: center;
    }

    .btn-decline {
      width: 128px;
    }

    .btn-approve {
      width: 128px;
    }

    .btn-delete, .btn-edit {
      width: 100px;
    }
  }

  .info-icon {
      font-size: 16px;
      margin-right: 8px;
      vertical-align: middle;
    }
}

.time-off-icon {
  width: 20px;
  height: 20px;
}

.approval-section {
  .approval-status, .pending-status {
    font-size: 13px;
    font-weight: 500;
    line-height: 20px;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .approval-status {
    color: #0052CC;

    .approved-by {
      color: $body-color;
    }
  }
}
</style>
